export { withFallback };
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import React, { isValidElement, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { usePageContext } from 'vike-react/usePageContext';
function withFallback(Component, options, Error_) {
    let Loading;
    let Error;
    if (options && typeof options === 'object' && ('Loading' in options || 'Error' in options)) {
        Loading = options.Loading;
        Error = options.Error;
    }
    else if (typeof options !== 'object' || isValidElement(options)) {
        Loading = options;
        Error = Error_;
    }
    const ComponentWithFallback = (componentProps) => {
        const pageContext = usePageContext();
        let element = React.createElement(Component, { ...componentProps });
        if (Error) {
            const originalElement = element;
            element = (React.createElement(QueryErrorResetBoundary, null, ({ reset }) => {
                return (React.createElement(ErrorBoundary, { fallbackRender: ({ error: originalError, resetErrorBoundary }) => typeof Error === 'function' ? (React.createElement(Error, { ...componentProps, retry: createRetry(resetErrorBoundary, reset), error: createError(originalError) })) : (Error) }, originalElement));
            }));
        }
        if (Loading === undefined && pageContext.config.Loading?.component) {
            Loading = pageContext.config.Loading.component;
        }
        if (Loading !== false) {
            const originalElement = element;
            element = (React.createElement(Suspense, { fallback: typeof Loading === 'function' ? React.createElement(Loading, { ...componentProps }) : Loading }, originalElement));
        }
        return element;
    };
    ComponentWithFallback.displayName = `withFallback(${Component.displayName || Component.name || 'Component'})`;
    return ComponentWithFallback;
}
function getErrorMessage(error) {
    if (error && error instanceof Error) {
        return error.message;
    }
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'Unknown error';
}
function createError(originalError) {
    const message = getErrorMessage(originalError);
    const error = { message };
    if (typeof originalError === 'object') {
        Object.assign(error, originalError);
        for (const key of ['name', 'stack', 'cause']) {
            if (key in originalError) {
                Object.assign(error, { [key]: originalError[key] });
            }
        }
    }
    return error;
}
function createRetry(resetErrorBoundary, retryQueryFn) {
    return function retry(options = {}) {
        const { retryQuery = true } = options;
        if (retryQuery) {
            retryQueryFn();
        }
        resetErrorBoundary();
    };
}
